import { Row, Spin, Typography, Table, Card, Button, message, Popconfirm, Modal, Col, Radio } from "antd";
import { CheckCircleTwoTone, EditOutlined, LinkOutlined, DeleteOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import HTTPClient from "../../../utils/HTTPClient";

import moment from "moment";

import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LinkDomainModal } from "./LinkDomainModal";

const List = ({}) => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [freeTrialAvailable, setFreeTrialAvailable] = useState(false);
    const [showBuyModal, setShowBuyModal] = useState(false);
    const [openModalDomain, setOpenModalDomain] = useState(false);
    const [selectProjectId, setSelectProjectId] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [expirationDate, setExpirationDate] = useState(null);
    const [plan, setPlan] = useState("yearly");

  
    const { t } = useTranslation();

    const fetch = async () => {
        setLoading(true);

        try {
            let response = await HTTPClient.get("/builder/");
            if (response.status === 200) {
                const { free_trial_available, projects } = response?.data || {};
                setFreeTrialAvailable(free_trial_available);
                setProjects(projects || []);
                setLoading(false);

                return;
            } else if (response.status === 203) {
                response = await HTTPClient.post("/builder/start_builder");
                if (response.status === 200) {
                    response = await HTTPClient.get("/builder/");
                    const { free_trial_available, projects } = response?.data || {};
                    setFreeTrialAvailable(free_trial_available);
                    setProjects(projects || []);
                    setLoading(false);
                }
            }
            if (response.status !== 200) {
                navigate("/pro/home");
                message.error(t("builder.list.error"));
            }
        } catch (error) {
            if (error.response.status === 303) {
                window.location.replace(error.response.data);
                return;
            }
            navigate("/pro/home");
            message.error(t("builder.list.error"));
        }

        setLoading(false);
    };

    useEffect(() => {
        fetch();
    }, []);

    const handlePlanChange = (e) => {
        setPlan(e.target.value);
    };

    const deleteProject = async (project_id) => {
        try {
            await HTTPClient.delete(`/builder/projects/${project_id}`);
            message.success(t("builder.list.delete.success"));
            fetch();
        } catch (error) {
            message.error(t("builder.list.error"));
        }
    };

    const get_edit_url = async (project_id) => {
        setEditLoading(true);
        const response = await HTTPClient.get(`/builder/edit_url/${project_id}`);
        window.open(response.data, "_blank", "noopener,noreferrer");
        setEditLoading(false);
    };

    const buyProject = async (subscription_type) => {
        const body = {
            subscription_type,
            project_id: selectProjectId,
        };
        try {
            await HTTPClient.post("/stripe/create_builder_checkout_session", body);
            message.success(t("builder.list.update.success"));
            fetch();
        } catch (e) {
            // redirect to checkout portal
            if (e.response.status === 303) {
                window.location.replace(e.response.data);
                return;
            }
            const { error_code } = e?.response?.data;
            message.error(t([`errors.${error_code}`, "builder.list.error"]));
        }
    };

    const openModal = (project_id) => {
        setOpenModalDomain(true);
        setSelectProjectId(project_id);
    };

    const closeModal = (doFetch) => {
        setOpenModalDomain(false);
        setSelectProjectId(null);
        if (doFetch) { fetch() }
    };

    const renderStatus = (_, record) => {
        const { free_trial_expiration } = record;
        if (!free_trial_expiration) {
            return (
                <Typography.Text style={{ color: "green" }}>
                    {" "}
                    {t("builder.list.pro")}{" "}
                </Typography.Text>
            );
        }

        return (
            <Typography.Text>
                {isLargeScreen ? t("builder.list.free") + moment(free_trial_expiration).format("DD/MM/YYYY") : "Free"}
            </Typography.Text>
        );
    };

    const renderPreviewUrl = (_, record) => {
        return (
            <Typography.Text>
                <a
                    style={{
                        cursor: "pointer",
                        fontWeight: "bold",
                    }}
                    href={record.preview_url}
                    target="_blank"
                    rel="noreferrer">
                    <Typography.Text strong>
                        {isLargeScreen ? record.preview_url.replace("https://", "") : <Button><LinkOutlined /></Button>}
                    </Typography.Text>{" "}
                </a>
            </Typography.Text>
        );
    };

    const renderActions = (_, record) => {
        return (
            <Typography.Text style={{ display: "flex", gap: "10px" }}>
                <Button
                    type="primary"
                    onClick={() => get_edit_url(record.project_id)}
                    loading={editLoading}>
                    {isLargeScreen ? t("builder.list.actions.edit") : <EditOutlined />}
                </Button>
                {!record.free_trial_expiration && (
                    <Button
                        type="primary"
                        onClick={() => openModal(record.project_id)}
                        loading={editLoading}>
                        {isLargeScreen ? t("builder.list.actions.attach_domain") : "DNS"}
                    </Button>
                )}

                {record.free_trial_expiration && (
                    <>
                        <Button
                            type="primary"
                            onClick={() => {
                                setSelectProjectId(record.project_id);
                                setExpirationDate(record.free_trial_expiration);
                                setShowBuyModal(true);
                            }}
                            loading={editLoading}>
                            {isLargeScreen ? t("builder.list.actions.go_pro") : "PRO"}
                        </Button>
                        <Popconfirm
                            title={t("builder.list.delete.title")}
                            onConfirm={() => deleteProject(record.project_id)}
                            okText={t("builder.list.delete.ok")}
                            cancelText="No">
                            <Button
                                danger
                                loading={editLoading}>
                                {isLargeScreen ? t("builder.list.actions.delete") : <DeleteOutlined />}
                            </Button>
                        </Popconfirm>
                    </>
                )}
            </Typography.Text>
        );
    };

    const dataColumns = [
        {
            title: t("builder.list.fields.url"),
            dataIndex: "preview_url",
            key: "preview_url",
            render: renderPreviewUrl,
        },
        {
            title: t("builder.list.fields.status"),
            dataIndex: "status",
            key: "status",
            render: renderStatus,
        },
        {
            title: t("builder.list.fields.actions"),
            dataIndex: "",
            key: "actions",
            render: renderActions,
        },
    ];

    const isLargeScreen = useMediaQuery({
        query: "(min-width: 771px)",
    });

    return (
        <Row style={{ padding: isLargeScreen ? "20px 50px" : "10px 10px" }}>
            <Row>
                <Typography.Text style={{ fontSize: "36px", fontWeight: "bold" }}>
                    {t("builder.list.title")}
                </Typography.Text>
            </Row>
            <Row style={{ width: "100%", marginTop: "10px" }}>
                <Typography.Text>{t("builder.list.subtitle")}</Typography.Text>
            </Row>
            {loading ? (
                <div
                    style={{
                        width: "100%",
                        minHeight: "20vh",
                        overflow: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                    <Spin />
                </div>
            ) : (
                <Row style={{ width: "100%" }}>
                    {projects.length ? (
                        <div style={{ width: "100%", margin: "60px 0" }}>
                            <Table
                                columns={dataColumns}
                                dataSource={projects}
                                pagination={false}
                                style={{ marginTop: "25px", width: "100%", overflow: "auto" }}
                            />

                            <Button
                                type="primary"
                                style={{ marginTop: "25px" }}
                                onClick={() => navigate("/pro/builder/create")}>
                                {t("builder.list.call_to_action")}
                            </Button>
                        </div>
                    ) : (
                        <Card style={{ width: "100%", marginTop: "25px" }}>
                            <Typography.Text>{t("builder.list.empty")}</Typography.Text>
                            <div>
                                <Button
                                    type="primary"
                                    style={{ marginTop: "10px" }}
                                    onClick={() => navigate("/pro/builder/create")}>
                                    {t("builder.list.call_to_action")}
                                </Button>
                            </div>
                        </Card>
                    )}
                </Row>
            )}
            {openModalDomain && (
                <LinkDomainModal
                    open={openModalDomain}
                    closeModal={closeModal}
                    projectId={selectProjectId}
                />
            )}

            {showBuyModal && (
                <Modal
                    title={t("builder.list.go_pro_modal.title")}
                    open={showBuyModal}
                    footer={null}
                    onCancel={() => setShowBuyModal(false)}>
                    <Row>
                        <Typography.Text>{t("builder.list.go_pro_modal.text")}</Typography.Text>
                    </Row>
                    <Row align="middle" style={{ textAlign: "center" }}>
                        <Col span={8}>
                            <img src="/hurryup.png" alt="Hurry up" style={{ height: "120px" }} />                        
                        </Col>
                        <Col>
                            <Row>
                                <Typography.Text>{t("builder.list.go_pro_modal.text2")+" "+moment(expirationDate).format("DD/MM/YYYY")}</Typography.Text>
                            </Row>
                            <Row>
                                <Typography.Text>{t("builder.list.go_pro_modal.text3")}</Typography.Text>
                            </Row>
                        </Col>
                    </Row>
                    <Row style={{ display: "flex", justifyContent: "center"}}>
                        <Radio.Group onChange={handlePlanChange} value={plan} buttonStyle="solid">
                            <Radio.Button value="monthly">{t("builder.list.go_pro_modal.month")}</Radio.Button>
                            <Radio.Button value="yearly">{t("builder.list.go_pro_modal.year")}</Radio.Button>
                        </Radio.Group>
                    </Row>
                    <Row style={{ marginTop: "40px", display: "flex", justifyContent: "center" }}>
                        <Col span={16}>
                            <Row>
                                <Col span={3}></Col>
                                <Col span={4}>
                                    <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "26px" }} />                        
                                </Col>
                                <Col span={15}>
                                    <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{plan==="monthly" ? t("builder.list.go_pro_modal.pro1a") : t("builder.list.go_pro_modal.pro1b") }</Typography.Text>
                                </Col>
                            </Row>
                            <Row style={{ marginTop: "15px" }}>
                                <Col span={3}></Col>
                                <Col span={4}>
                                    <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "26px" }} />                        
                                </Col>
                                <Col span={15}>
                                    <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("builder.list.go_pro_modal.pro2")}</Typography.Text>
                                </Col>
                            </Row>
                            <Row style={{ marginTop: "15px" }}>
                                <Col span={3}></Col>    
                                <Col span={4}>
                                    <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "26px" }} />                        
                                </Col>
                                <Col span={15}>
                                    <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("builder.list.go_pro_modal.pro3")}</Typography.Text>
                                </Col>
                            </Row>
                            {plan === "yearly" && 
                                <Row style={{ marginTop: "10px" }}>
                                    <Col span={3}></Col>    
                                    <Col span={4}>
                                        <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "26px" }} />                        
                                    </Col>
                                    <Col span={15}>
                                        <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("builder.list.go_pro_modal.pro4")}</Typography.Text>
                                    </Col>
                                </Row>    
                            }
                        </Col>
                    </Row>
                    <Row style={{ marginTop: "40px", display: "flex", justifyContent: "center" }}>
                        <Button type="primary" onClick={() => buyProject(plan)}>
                            {t("builder.create.buy_modal.call_to_action")}
                        </Button>
                    </Row>
                </Modal>
            )}
        </Row>
    );
};

export { List };
